import { useState } from "react";
import { ArrowRight, Sparkles, Users, Clock, ShoppingCart, RotateCcw } from "lucide-react";

const PROPERTY_TYPES = [
  { value: "Apartment",  mod: 1.00, label: "Apartment Community" },
  { value: "Office",     mod: 1.20, label: "Corporate Office" },
  { value: "Gym",        mod: 0.80, label: "Gym / Fitness" },
  { value: "Hotel",      mod: 0.90, label: "Hotel" },
  { value: "Medical",    mod: 0.60, label: "Medical Office" },
  { value: "School",     mod: 1.10, label: "School / University" },
  { value: "Warehouse",  mod: 1.30, label: "Warehouse / Industrial" },
  { value: "Other",      mod: 1.00, label: "Other" },
];

const TRAFFIC_LEVELS = [
  { value: "low",    label: "Low",    factor: 0.15, satBoost: 0,  desc: "Quieter common areas" },
  { value: "medium", label: "Medium", factor: 0.30, satBoost: 3,  desc: "Steady daily traffic" },
  { value: "high",   label: "High",   factor: 0.50, satBoost: 6,  desc: "Busy hub or amenity" },
];

const fmt = (n) => new Intl.NumberFormat("en-US").format(Math.round(n));

export default function Calculator() {
  const [type, setType]       = useState(PROPERTY_TYPES[0].value);
  const [headcount, setHc]    = useState(150);
  const [traffic, setTraffic] = useState("medium");
  const [shown, setShown]     = useState(false);

  const property = PROPERTY_TYPES.find((p) => p.value === type);
  const trafficCfg = TRAFFIC_LEVELS.find((t) => t.value === traffic);

  const compute = () => {
    const hc = Math.max(Number(headcount) || 0, 1);
    const dailyTx = hc * trafficCfg.factor * property.mod;
    const weeklyTx = dailyTx * 7;
    const annualTx = dailyTx * 365;
    const hoursSavedAnnual = (annualTx * 12) / 60; // 12 min per off-site convenience trip
    return { weeklyTx, annualTx, hoursSavedAnnual };
  };

  const calc = compute();

  const onSubmit = (e) => {
    e.preventDefault();
    setShown(true);
    // Smooth scroll to results
    setTimeout(() => {
      document.getElementById("calc-results")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  const reset = () => {
    setShown(false);
    setHc(150);
    setType(PROPERTY_TYPES[0].value);
    setTraffic("medium");
  };

  return (
    <section id="calculator" className="bg-cravy-cream2 py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-5 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cravy-orange/15 border border-cravy-orange/30 text-cravy-orangeDeep text-[12px] font-semibold tracking-wide uppercase">
            <Sparkles className="w-3.5 h-3.5" /> Quick Cravy Estimate
          </div>
          <h2 className="mt-6 font-display font-extrabold text-[40px] sm:text-[56px] lg:text-[64px] leading-[0.95] tracking-[-0.03em]">
            What would Cravy do for <span className="italic text-cravy-orangeDeep">your property?</span>
          </h2>
          <p className="mt-5 text-cravy-ink/70 text-[16px] lg:text-[17px] leading-relaxed">
            A quick estimate of weekly transactions and resident time saved at your property, based on industry-standard convenience traffic for similar buildings.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 items-stretch">
          {/* Inputs */}
          <form onSubmit={onSubmit} className="lg:col-span-5 rounded-3xl bg-white border border-cravy-ink/8 p-7 lg:p-8 shadow-sm">
            <div className="text-cravy-orangeDeep font-semibold text-[11px] uppercase tracking-[0.18em] mb-6">Your property</div>

            <label className="block mb-5">
              <span className="block text-cravy-ink/70 text-[12px] uppercase tracking-[0.15em] mb-2 font-semibold">Property type</span>
              <select value={type} onChange={(e) => setType(e.target.value)} className="cravy-input-light" data-testid="calc-type">
                {PROPERTY_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </label>

            <label className="block mb-5">
              <span className="block text-cravy-ink/70 text-[12px] uppercase tracking-[0.15em] mb-2 font-semibold">
                People on site <span className="text-cravy-ink/45 font-normal normal-case tracking-normal">— residents, employees, or guests</span>
              </span>
              <input
                type="number" min="1" max="100000" value={headcount}
                onChange={(e) => setHc(e.target.value)}
                className="cravy-input-light"
                data-testid="calc-headcount"
              />
            </label>

            <div className="block mb-7">
              <span className="block text-cravy-ink/70 text-[12px] uppercase tracking-[0.15em] mb-3 font-semibold">Daily foot traffic</span>
              <div className="grid grid-cols-3 gap-2">
                {TRAFFIC_LEVELS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setTraffic(t.value)}
                    className={`px-3 py-3 rounded-2xl border text-left transition ${
                      traffic === t.value
                        ? "bg-cravy-ink text-cravy-cream border-cravy-ink"
                        : "bg-white text-cravy-ink border-cravy-ink/10 hover:border-cravy-orange/50"
                    }`}
                    data-testid={`calc-traffic-${t.value}`}
                  >
                    <div className="font-display font-bold text-[15px]">{t.label}</div>
                    <div className={`text-[11px] mt-0.5 ${traffic === t.value ? "text-cravy-cream/65" : "text-cravy-ink/50"}`}>{t.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <button type="submit" className="btn-orange w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-[15px]" data-testid="calc-submit">
              {shown ? "Recalculate" : "Show my numbers"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          {/* Results */}
          <div id="calc-results" className="lg:col-span-7 rounded-3xl bg-cravy-ink text-cravy-cream p-7 lg:p-8 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-[320px] h-[320px] rounded-full bg-cravy-orange/15 blur-[100px]" />

            {!shown ? (
              <div className="relative h-full flex flex-col items-center justify-center text-center py-16 lg:py-0" data-testid="calc-empty">
                <div className="w-14 h-14 rounded-2xl bg-cravy-orange/15 text-cravy-orange flex items-center justify-center mb-5">
                  <ShoppingCart className="w-6 h-6" />
                </div>
                <div className="font-display font-bold text-[22px] tracking-tight">Your estimate, ready in 1 click</div>
                <div className="mt-2 text-cravy-cream/55 text-[14px] max-w-sm">
                  Hit "Show my numbers" and we'll project your weekly transactions and the resident time we'd save vs off-site convenience trips.
                </div>
              </div>
            ) : (
              <div className="relative" data-testid="calc-result">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div>
                    <div className="text-cravy-orange font-semibold text-[11px] uppercase tracking-[0.18em]">Projected for your property</div>
                    <div className="mt-2 font-display font-extrabold text-[22px] tracking-tight">
                      {property.label} · {fmt(headcount)} people · {trafficCfg.label.toLowerCase()} traffic
                    </div>
                  </div>
                  <button onClick={reset} className="shrink-0 inline-flex items-center gap-1.5 text-cravy-cream/55 hover:text-cravy-cream text-[12px]" data-testid="calc-reset">
                    <RotateCcw className="w-3.5 h-3.5" /> Reset
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <Stat icon={ShoppingCart} label="Weekly transactions"   value={fmt(calc.weeklyTx)} hint="Items sold per week" />
                  <Stat icon={Users}        label="Annual transactions"   value={fmt(calc.annualTx)} hint="Items sold per year" />
                  <Stat icon={Clock}        label="Hours saved per year"  value={`${fmt(calc.hoursSavedAnnual)} hrs`} hint="vs off-site convenience runs" highlight />
                </div>

                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                  <div className="text-cravy-cream/75 text-[14px] leading-relaxed">
                    A <strong>zero-cost</strong> amenity that, at your scale, would handle around{" "}
                    <strong className="text-cravy-orange">{fmt(calc.annualTx)} convenience purchases per year</strong> for your community.
                  </div>
                  <button
                    onClick={() => document.getElementById("partner")?.scrollIntoView({ behavior: "smooth" })}
                    className="btn-orange mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px]"
                    data-testid="calc-cta-partner"
                  >
                    Request a market for {property.label} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="mt-4 text-cravy-cream/35 text-[11px] leading-relaxed">
                  Rough estimate using industry-standard convenience-traffic rates ({trafficCfg.factor.toFixed(2)} visits per person per day) and an average 12-minute off-site convenience trip. Real numbers vary with product mix, location, and seasonality.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, label, value, hint, highlight }) {
  return (
    <div className={`rounded-2xl p-5 border ${highlight ? "bg-cravy-orange/15 border-cravy-orange/30" : "bg-white/[0.04] border-white/8"}`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${highlight ? "text-cravy-orange" : "text-cravy-cream/55"}`} />
        <div className={`text-[11px] uppercase tracking-[0.13em] font-semibold ${highlight ? "text-cravy-orange" : "text-cravy-cream/55"}`}>{label}</div>
      </div>
      <div className={`mt-3 font-display font-extrabold text-[34px] lg:text-[40px] leading-none tracking-tight ${highlight ? "text-cravy-orange" : "text-cravy-cream"}`}>{value}</div>
      <div className="mt-1.5 text-cravy-cream/45 text-[12px]">{hint}</div>
    </div>
  );
}
